import React, { useState } from 'react';
import SbEditable from 'storyblok-react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Sentry from '@sentry/react';
import InputMask from 'react-input-mask';
import * as Yup from 'yup';

import { leads, metrics } from '../../services';
import TrackFormDirty from '../TrackFormDirty';
import { PhoneRegex } from '../../constants';
import './partner-form.scss';

const defaultFormValues = {
  company: '',
  website: '',
  address: '',
  zip: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  description: '',
  acceptTerms: false,
  interest: [],
};

const CompanyInfo = () => {
  return (
    <>
      <h4>Company Info:</h4>
      <div className="fields">
        <div className="field">
          <Field name="company" placeholder="Company Name" />
          <ErrorMessage className="error" component="div" name="company" />
        </div>
        <div className="field">
          <Field name="website" placeholder="Website" />
          <ErrorMessage className="error" component="div" name="website" />
        </div>
      </div>
      <div className="fields">
        <div className="field">
          <Field name="address" placeholder="Street Address" />
          <ErrorMessage className="error" component="div" name="address" />
        </div>
        <div className="field">
          <Field name="zip" placeholder="Zip Code">
            {({ field }) => (
              <InputMask
                {...field}
                mask="99999"
                placeholder="Zip Code"
                type="text"
                alwaysShowMask={false}
              />
            )}
          </Field>
          <ErrorMessage className="error" component="div" name="zip" />
        </div>
      </div>
    </>
  );
};

const PrimaryContact = () => {
  return (
    <>
      <h4>Primary Contact:</h4>
      <div className="fields">
        <div className="field">
          <Field name="firstName" placeholder="First Name" />
          <ErrorMessage className="error" component="div" name="firstName" />
        </div>
        <div className="field">
          <Field name="lastName" placeholder="Last Name" />
          <ErrorMessage className="error" component="div" name="lastName" />
        </div>
      </div>
      <div className="fields">
        <div className="field">
          <Field name="phone" placeholder="Phone">
            {({ field }) => (
              <InputMask
                {...field}
                mask="(999) 999-9999"
                placeholder="Phone"
                type="text"
                alwaysShowMask={false}
              />
            )}
          </Field>
          <ErrorMessage className="error" component="div" name="phone" />
        </div>
        <div className="field">
          <Field name="email" placeholder="Email" />
          <ErrorMessage className="error" component="div" type="email" name="email" />
        </div>
      </div>
    </>
  );
};

const Message = () => {
  return (
    <>
      <h4>Message:</h4>
      <div className="field">
        <Field name="description" as="textarea" rows="6" />
      </div>
    </>
  );
};

const PartnerForm = (props) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [submitState, setSubmitState] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const onSubmit = async (values) => {
    setSubmitState('');

    try {
      await leads.submitPartner({
        ...values,
        referred_by: '',
        type: '',
      });

      setSubmitState('success');
      metrics.trackPartnerFormSubmit(
        window.location.origin + window.location.pathname,
        'PartnerLeadForm'
      );
    } catch (e) {
      Sentry.captureException(e);
      setErrorMessage('We had a problem processing your request. Please try again.');
      setSubmitState('failed');
    }
  };

  const containerClasses = ['partner-f'];
  containerClasses.push('theme-' + props.blok.theme);

  const title = props.blok.title || '';
  const submitButtonText = props.blok.submit_button_text || 'Submit';
  const submittingButtonText = props.blok.submitting_button_text || 'Submitting...';
  const buttonStyle = props.blok.button_style || 'blue';
  const successMessage = props.blok.success_message || 'Thank you, we will be in touch soon.';
  const displayMode = props.blok.display_mode || 'one_column';

  return (
    <SbEditable content={props.blok}>
      <div className={containerClasses.join(' ')}>
        {submitState !== 'success' && (
          <Formik
            initialValues={defaultFormValues}
            onSubmit={onSubmit}
            validationSchema={Yup.object().shape({
              company: Yup.string().required('Required'),
              website: Yup.string().required('Required'),
              address: Yup.string().required('Required'),
              zip: Yup.string().required('Required').matches(/\d{5}/, 'Enter your ZIP code'),
              firstName: Yup.string().required('Required'),
              lastName: Yup.string().required('Required'),
              phone: Yup.string()
                .required('Required')
                .matches(PhoneRegex, 'Enter your phone number'),
              email: Yup.string().required('Required').email('Enter a valid e-mail address'),
              acceptTerms: Yup.bool()
                .required('Please read and agree to the Mutual NDA')
                .oneOf([true], 'Please read and agree to the Mutual NDA'),
            })}
          >
            {(formik) => {
              const buttonText = formik.isSubmitting ? submittingButtonText : submitButtonText;

              return (
                <Form className="input-form">
                  <TrackFormDirty
                    formComponent="PartnerForm"
                    isDirty={isDirty}
                    setIsDirty={setIsDirty}
                  />
                  <div className={displayMode === 'three_column' ? 'row align-items-start' : ''}>
                    <div className={displayMode === 'three_column' ? 'col-12 col-xl-4' : ''}>
                      {props.blok.interests && props.blok.interests.length > 0 && (
                        <>
                          <h4>I'm interested in: (check all that apply)</h4>
                          {props.blok.interests.map((val, idx) => (
                            <div className="interest" key={`interest-${idx}`}>
                              <Field
                                type="checkbox"
                                name="interest"
                                id={`interest-${idx}`}
                                value={val.value}
                              />
                              <label htmlFor={`interest-${idx}`}>{val.name}</label>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                    {displayMode === 'three_column' && (
                      <>
                        <div className="row col-12 col-xl-8">
                          <div className="col-12 col-xl-6">
                            <CompanyInfo></CompanyInfo>
                          </div>
                          <div className="col-12 col-xl-6">
                            <PrimaryContact></PrimaryContact>
                          </div>
                          <div className="col-12">
                            <Message></Message>
                          </div>
                        </div>
                      </>
                    )}
                    {displayMode !== 'three_column' && (
                      <>
                        <div>
                          <CompanyInfo></CompanyInfo>
                        </div>
                        <div>
                          <PrimaryContact></PrimaryContact>
                        </div>
                        <div>
                          <Message></Message>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="terms">
                    <Field type="checkbox" name="acceptTerms" id="acceptTerms" />
                    <label htmlFor="acceptTerms">
                      I have read and agree to the{' '}
                      <a href="https://a.storyblok.com/f/89720/x/ba62a39102/swell_mutual_nda.pdf">
                        Mutual Non-Disclosure Agreement
                      </a>
                    </label>
                    <ErrorMessage className="error" component="div" name="acceptTerms" />
                  </div>
                  {submitState === 'failed' && <div className="error">{errorMessage}</div>}
                  <button
                    className={`btn-${buttonStyle}`}
                    disabled={formik.isSubmitting}
                    type="submit"
                  >
                    {buttonText}
                  </button>
                </Form>
              );
            }}
          </Formik>
        )}
        {submitState === 'success' && <div className="success">{successMessage}</div>}
      </div>
    </SbEditable>
  );
};

export default PartnerForm;
